import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import TextField from "@material-ui/core/TextField";
import { toast } from "react-toastify";

import api from "../../../services/api";
import toastError from "../../../errors/toastError";

const useStyles = makeStyles(theme => ({
	root: {
		padding: theme.spacing(4),
	},
	header: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		marginBottom: theme.spacing(2),
	},
	paper: {
		width: "100%",
	},
	statCard: {
		padding: theme.spacing(2),
		textAlign: "center",
	},
	statValue: {
		fontWeight: 700,
	},
	statsRow: {
		marginBottom: theme.spacing(3),
	},
	form: {
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing(2),
	},
}));

const emptyTenant = { name: "", plan: "free", maxConnections: 1, maxUsers: 5 };
const emptySubowner = { name: "", email: "", password: "" };

const roleLabel = {
	owner: "Dono",
	subowner: "Sub-Dono",
	agent: "Atendente",
};

const StatCard = ({ classes, label, value }) => (
	<Grid item xs={6} sm={4} md={2}>
		<Paper className={classes.statCard} variant="outlined">
			<Typography variant="h5" className={classes.statValue}>
				{value}
			</Typography>
			<Typography variant="body2" color="textSecondary">
				{label}
			</Typography>
		</Paper>
	</Grid>
);

const Tenants = () => {
	const classes = useStyles();
	const history = useHistory();
	const [tenants, setTenants] = useState([]);
	const [totals, setTotals] = useState(null);
	const [server, setServer] = useState(null);
	const [tenantModalOpen, setTenantModalOpen] = useState(false);
	const [subownerModalOpen, setSubownerModalOpen] = useState(false);
	const [usersModalOpen, setUsersModalOpen] = useState(false);
	const [selectedTenant, setSelectedTenant] = useState(null);
	const [tenantForm, setTenantForm] = useState(emptyTenant);
	const [subownerForm, setSubownerForm] = useState(emptySubowner);

	const fetchStats = async () => {
		try {
			const { data } = await api.get("/tenants/stats");
			setTenants(data.tenants);
			setTotals(data.totals);
			setServer(data.server);
		} catch (err) {
			toastError(err);
		}
	};

	useEffect(() => {
		fetchStats();
	}, []);

	const handleCreateTenant = async e => {
		e.preventDefault();
		try {
			await api.post("/tenants", tenantForm);
			toast.success("Empresa criada com sucesso.");
			setTenantModalOpen(false);
			setTenantForm(emptyTenant);
			fetchStats();
		} catch (err) {
			toastError(err);
		}
	};

	const openSubownerModal = tenant => {
		setSelectedTenant(tenant);
		setSubownerForm(emptySubowner);
		setSubownerModalOpen(true);
	};

	const openUsersModal = tenant => {
		setSelectedTenant(tenant);
		setUsersModalOpen(true);
	};

	const handleCreateSubowner = async e => {
		e.preventDefault();
		try {
			await api.post(`/tenants/${selectedTenant.id}/subowner`, subownerForm);
			toast.success("Sub-Dono criado com sucesso.");
			setSubownerModalOpen(false);
			fetchStats();
		} catch (err) {
			toastError(err);
		}
	};

	const formatUptime = seconds => {
		if (!seconds) return "-";
		const h = Math.floor(seconds / 3600);
		const m = Math.floor((seconds % 3600) / 60);
		return `${h}h ${m}m`;
	};

	return (
		<div className={classes.root}>
			<Container maxWidth="lg">
				<div className={classes.header}>
					<Typography variant="h5">Painel do Dono</Typography>
					<Button
						variant="contained"
						color="primary"
						onClick={() => setTenantModalOpen(true)}
					>
						Nova Empresa
					</Button>
				</div>

				{totals && (
					<Grid container spacing={2} className={classes.statsRow}>
						<StatCard classes={classes} label="Empresas" value={totals.tenants} />
						<StatCard classes={classes} label="Usuários" value={totals.users} />
						<StatCard classes={classes} label="Tickets" value={totals.tickets} />
						<StatCard classes={classes} label="Mensagens" value={totals.messages} />
						<StatCard classes={classes} label="Conexões" value={totals.connections} />
						<StatCard classes={classes} label="Contatos" value={totals.contacts} />
					</Grid>
				)}

				{server && (
					<Typography variant="body2" color="textSecondary" gutterBottom>
						Servidor: Node {server.nodeVersion} · {server.platform} · no ar há{" "}
						{formatUptime(server.uptimeSeconds)}
					</Typography>
				)}

				<Paper className={classes.paper} variant="outlined">
					<Table>
						<TableHead>
							<TableRow>
								<TableCell>Nome</TableCell>
								<TableCell>Plano</TableCell>
								<TableCell align="center">Conexões</TableCell>
								<TableCell align="center">Usuários</TableCell>
								<TableCell align="center">Tickets</TableCell>
								<TableCell align="center">Contatos</TableCell>
								<TableCell>Status</TableCell>
								<TableCell align="center">Ações</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{tenants.map(tenant => (
								<TableRow key={tenant.id}>
									<TableCell>{tenant.name}</TableCell>
									<TableCell>{tenant.plan}</TableCell>
									<TableCell align="center">
										{tenant.connectionsCount}/{tenant.maxConnections}
									</TableCell>
									<TableCell align="center">
										{tenant.users.length}/{tenant.maxUsers}
									</TableCell>
									<TableCell align="center">{tenant.ticketsCount}</TableCell>
									<TableCell align="center">{tenant.contactsCount}</TableCell>
									<TableCell>{tenant.status}</TableCell>
									<TableCell align="center">
										<Button
											size="small"
											onClick={() => history.push(`/admin/tenants/${tenant.id}/chats`)}
										>
											Ver chats
										</Button>
										<Button size="small" onClick={() => openUsersModal(tenant)}>
											Ver usuários
										</Button>
										<Button size="small" onClick={() => openSubownerModal(tenant)}>
											Criar Sub-Dono
										</Button>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</Paper>
			</Container>

			<Dialog open={tenantModalOpen} onClose={() => setTenantModalOpen(false)}>
				<form onSubmit={handleCreateTenant}>
					<DialogTitle>Nova Empresa</DialogTitle>
					<DialogContent className={classes.form}>
						<TextField
							label="Nome da empresa"
							required
							fullWidth
							value={tenantForm.name}
							onChange={e => setTenantForm({ ...tenantForm, name: e.target.value })}
						/>
						<TextField
							label="Nº máximo de conexões WhatsApp"
							type="number"
							fullWidth
							value={tenantForm.maxConnections}
							onChange={e =>
								setTenantForm({ ...tenantForm, maxConnections: +e.target.value })
							}
						/>
						<TextField
							label="Nº máximo de usuários"
							type="number"
							fullWidth
							value={tenantForm.maxUsers}
							onChange={e =>
								setTenantForm({ ...tenantForm, maxUsers: +e.target.value })
							}
						/>
					</DialogContent>
					<DialogActions>
						<Button onClick={() => setTenantModalOpen(false)}>Cancelar</Button>
						<Button type="submit" color="primary" variant="contained">
							Criar
						</Button>
					</DialogActions>
				</form>
			</Dialog>

			<Dialog open={subownerModalOpen} onClose={() => setSubownerModalOpen(false)}>
				<form onSubmit={handleCreateSubowner}>
					<DialogTitle>
						Criar Sub-Dono para {selectedTenant?.name}
					</DialogTitle>
					<DialogContent className={classes.form}>
						<TextField
							label="Nome"
							required
							fullWidth
							value={subownerForm.name}
							onChange={e =>
								setSubownerForm({ ...subownerForm, name: e.target.value })
							}
						/>
						<TextField
							label="Email"
							type="email"
							required
							fullWidth
							value={subownerForm.email}
							onChange={e =>
								setSubownerForm({ ...subownerForm, email: e.target.value })
							}
						/>
						<TextField
							label="Senha"
							type="password"
							required
							fullWidth
							value={subownerForm.password}
							onChange={e =>
								setSubownerForm({ ...subownerForm, password: e.target.value })
							}
						/>
					</DialogContent>
					<DialogActions>
						<Button onClick={() => setSubownerModalOpen(false)}>Cancelar</Button>
						<Button type="submit" color="primary" variant="contained">
							Criar
						</Button>
					</DialogActions>
				</form>
			</Dialog>

			<Dialog
				open={usersModalOpen}
				onClose={() => setUsersModalOpen(false)}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>Usuários de {selectedTenant?.name}</DialogTitle>
				<DialogContent>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Nome</TableCell>
								<TableCell>Email</TableCell>
								<TableCell>Papel</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{selectedTenant?.users?.map(user => (
								<TableRow key={user.id}>
									<TableCell>{user.name}</TableCell>
									<TableCell>{user.email}</TableCell>
									<TableCell>{roleLabel[user.role] || user.role}</TableCell>
								</TableRow>
							))}
							{selectedTenant?.users?.length === 0 && (
								<TableRow>
									<TableCell colSpan={3} align="center">
										Nenhum usuário nessa empresa ainda.
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setUsersModalOpen(false)}>Fechar</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
};

export default Tenants;
